/* eslint-disable max-len */
import { queryPlatformDetail } from '@/src/api/platform'
import { BLOG_SLUG, defaultContent, formatUrl, SUPPORT_LANGUAGE } from 'config/platform'
import Image from 'next/image'
// import { useEffect } from 'react'
import FaqList from 'src/components/FaqList'
import Layout from 'src/components/layout'
import MetaSeo from 'src/components/layout/metaSeo'
import Banner from 'src/components/platform/Banner'
import DefaultIcon from 'src/components/platform/DefaultIcon'
import Product from 'src/components/platform/Product'
import Search from 'src/components/platform/Search'
import useBatchNums from 'src/hooks/business/use_batch_nums'
import useGA from 'src/hooks/use_ga'
import useI18n from 'src/hooks/use_i18n'
import { CarrierInfoMarkDTO } from 'src/types'
import { carrierInfoMarkMap } from 'src/utils/carriers'
import { getV5Front } from 'src/utils/server_global'
import { DEFAULT_CARRIER_INFO_MARK } from '../../constants/carrier_info_mark'
import styles from './index.module.scss'
const logger = require('@/config/logger')

interface PageProps {
  carrierInfoMark: CarrierInfoMarkDTO
  country: string | any
  totalCarriersStr: string
  platformDetail: any
  isBlog: boolean // 是否需要处理为blog文章--> 去掉H1后的Order Tracking 以及搜索框
}

export default function PlatformList({ carrierInfoMark, isBlog, country, platformDetail }: PageProps) {
  useGA('brands_info')
  // console.log(platformDetail, 'platformDetail')
  const { activeLocale: locale, $t, i18nFormatAll } = useI18n()
  const {
    about,
    content,
    title,
    keywords,
    description,
    platform,
    external_url,
    alt_label,
    cover_image,
    subtitle,
    head_title,
    faq,
    template
  } = platformDetail || {}
  const { language, faq: tFaq, template_content } = template || {}
  const { getNumbers } = useBatchNums() // 批量处理运输单号
  const defaultAlt = `logo of ${platform}`

  // 点击搜索
  const handleSearch = (value) => {
    getNumbers(value, (data) => {
      if (data.success) {
        window.ga?.sendEvent?.('功能操作', 'platform-查询按钮', data.filterNumsArr.length.toString())
        window.location.href = `https://t.17track.net/${locale}#nums=${data.filterNumsArr.join(',')}`
      }
    })
  }

  // 对富文本中包含data-href属性的图片做跳转事件
  const handleClick = (e) => {
    const { nodeName, dataset } = e.target || {}
    if (nodeName === 'IMG' && dataset.href) {
      e.preventDefault()
      window.ga?.sendEvent?.('功能操作', 'platform-点击跳转', dataset.href)
      window.open(dataset.href, '_blank')
    }
  }
  // useEffect(() => {
  //   const AD = require('@/plugins/ad_adapter/platform.js')
  //   AD?.() // 广告插入
  // }, [])

  const H1 = head_title || isBlog ? platform : i18nFormatAll($t('ResWwwPlatform.global.__DetailH1'), { platform })
  const meta = {
    title: title || i18nFormatAll($t('ResWwwPlatform.global.__DetailTitle'), { platform }),
    keywords: keywords || `${platform},${H1}`,
    description: description || i18nFormatAll($t('ResWwwPlatform.global.__DetailDescription'), { platform })
  }
  return (
    <>
      <MetaSeo {...meta} />
      <Layout showAllPlatform carrierInfoMark={carrierInfoMark} country={country}>
        <div className={styles.detail}>
          <section className={styles.header}>
            <div className={styles.cardImg}>
              {cover_image ? (
                <Image loading="lazy" width={80} height={80} src={cover_image} alt={alt_label || defaultAlt} />
              ) : (
                <DefaultIcon size={80} name={platform} title={defaultAlt} />
              )}
            </div>
            <h1 id="yq_platform_name" title={H1}>
              {H1}
            </h1>
            <a href={formatUrl(external_url)} target="_blank" rel="nofollow">
              <span className="fa-globe"></span>
              {external_url}
            </a>
            {subtitle ? <p>{subtitle}</p> : null}
          </section>
          {isBlog ? null : (
            <Search
              placeholder={$t('ResWwwPlatform.global.__DetailSearchPlaceholder')}
              buttonText={$t('ResWwwPlatform.global.__DetailSearchBtn')}
              onSearch={handleSearch}
            />
          )}

          {/* 广告位-top */}
          {/* <section className={styles.gadContainer} id="DA_V6-WWW-BrandLabel"></section> */}
          <article>
            <h2>{i18nFormatAll($t('ResWwwPlatform.global.about'), { Brand: platform })}</h2>
            <p>{about}</p>
            {content && content !== '<p><br></p>' ? (
              <>
                <div
                  onClick={handleClick}
                  className={styles.content}
                  dangerouslySetInnerHTML={{
                    __html: content
                  }}
                ></div>
                <FaqList qaList={faq || []} />
              </>
            ) : language === locale && template_content && template_content !== '<p><br></p>' ? (
              <>
                <div
                  onClick={handleClick}
                  className={styles.content}
                  dangerouslySetInnerHTML={{
                    __html: template_content
                  }}
                ></div>
                <FaqList qaList={tFaq || []} />
              </>
            ) : (
              <FaqList qaList={[...defaultContent(platform), ...(faq || [])]} />
            )}
          </article>

          {/* 广告位-bottom */}
          {/* <section className={styles.gadContainer} id="DA_V6-WWW-BrandLabel"></section> */}
          <Product platform={platformDetail.platform} />
          <Banner />
        </div>
      </Layout>
    </>
  )
}

export async function getServerSideProps({ req, params, locale }) {
  try {
    const slug = params?.name || 'index'
    const { 'cf-ipcountry': country } = req.headers
    logger.info(`platform-${slug}Headers信息`, { headers: req.headers })

    // 语言不在支持列表中，重定向到英文
    if (!SUPPORT_LANGUAGE.includes(locale)) {
      return {
        redirect: {
          destination: req.originalUrl?.replace(locale, 'en'),
          permanent: true // or true, based on your requirement
          // statusCode: 301
        }
      }
    }

    const [V5Front, carrierInfoMark, platformRes] = await Promise.all([
      getV5Front('www', locale),
      fetch(`https://res.17track.net/asset/carrier/info/mark.${locale}.json`)
        .then((res) => res.json())
        .catch((error) => {
          logger.error('tracking页面获取carrierInfoMark信息异常', { message: error.message })
          return DEFAULT_CARRIER_INFO_MARK // 兜底数据
        }),
      queryPlatformDetail({ url: slug, language: locale }).catch((error) => {
        logger.error(`platform-${slug}详情页面获取platform数据异常`, { message: error.message })
        return null
      })
    ])
    // 没有数据或已下架，重定向到brands
    if (!platformRes?.data || !platformRes?.data?.id) {
      return {
        redirect: {
          destination: `/${locale}/brands`,
          permanent: false // or true, based on your requirement
        }
      }
    }

    logger.info(`platform-${slug}获取carrierInfoMark信息`, { carrierInfoMark })
    logger.info(`platform-${slug}获取详情信息`, platformRes)
    return {
      props: {
        locale,
        V5Front,
        platformDetail: platformRes?.data,
        country: country ?? 'CN',
        carrierInfoMark: carrierInfoMarkMap(carrierInfoMark),
        isBlog: BLOG_SLUG.includes(slug)
      }
    }
  } catch (error: any) {
    logger.error('platform详情页面请求异常', { message: error.message })
  }
}
